import React from 'react';

interface Stat {
  value: string;
  label: string;
}

export interface CtaButton {
  text: string;
  link: string;
  variant: 'primary' | 'outline';
  icon?: string;
}

export interface HeroSectionProps {
  title: React.ReactNode;
  subtitle: string;
  backgroundImage?: string;
  badge?: {
    text: string;
    icon: string;
  };
  stats?: Stat[];
  ctaButtons?: CtaButton[];
  children?: React.ReactNode;
  className?: string;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  title,
  subtitle,
  backgroundImage,
  badge,
  stats,
  ctaButtons,
  children,
  className = '',
}) => {
  const bgStyle = backgroundImage ? {
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('${backgroundImage}')`
  } : {};

  return (
    <section
      className={`relative py-24 bg-cover bg-center bg-no-repeat text-white ${className}`}
      style={bgStyle}
    >
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {badge && (
            <div className="flex items-center justify-center space-x-2 mb-6">
              <i className={`${badge.icon} text-white text-3xl`}></i>
              <span className="text-white/90 font-semibold text-xl">{badge.text}</span>
            </div>
          )}
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            {title}
          </h1>
          
          <p className="text-xl text-gray-200 mb-8 leading-relaxed max-w-3xl mx-auto">
            {subtitle}
          </p>

          {ctaButtons && ctaButtons.length > 0 && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              {ctaButtons.map((button, index) => (
                <a
                  key={index}
                  href={button.link}
                  className={`inline-flex items-center justify-center px-8 py-4 font-semibold text-lg rounded-lg transition-colors whitespace-nowrap ${
                    button.variant === 'primary' 
                      ? 'bg-red-600 hover:bg-red-700 text-white' 
                      : 'border-2 border-white text-white hover:bg-white hover:text-gray-900'
                  }`}
                >
                  {button.icon && <i className={`${button.icon} mr-2`}></i>}
                  {button.text}
                </a>
              ))}
            </div>
          )}

          {stats && stats.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                     <i className="ri-award-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">{stat.value}</h3>
                  <p className="text-white/80">{stat.label}</p>
                </div>
              ))}
            </div>
          )}
          
          {children}
        </div>
      </div>
    </section>
  );
};